/**
 * Support Tasks Database Service
 * Handles all database operations for support tasks
 */

import { supabase } from "./supabase";
import { logRpcError } from "./errorLog.service";
import { callWithRetry } from "../utils/transientError";
import {
  isSmsNumberBusy,
  retryOnSmsNumberBusy,
  SMS_NUMBER_BUSY_MESSAGE,
} from "../utils/smsNumberBusy";
import { matchesSupportTaskSearch } from "../utils/supportTaskSearch";
import { fetchAllRows } from "../utils/fetchAllRows";
import { resolveTaskTypeFilter } from "../types/supportTasks.types";
import {
  bucketSupportTaskTabCounts,
  pastDueCutoffISO,
  type CountableTask,
  type SupportTaskTabCounts,
} from "../utils/supportTaskTabCounts";

/**
 * 2026-05-27 (Item 2 Phase 1): tag UI writes with explicit
 * last_modified_source so the audit trigger doesn't have to fall
 * back to a default. last_modified_by is set by the trigger from
 * auth.uid() when the caller doesn't supply one — which is the
 * normal case for frontend writes via PostgREST.
 *
 * Use this helper for every .update() call against support_tasks
 * issued from the UI.
 */
const withCrmUserAudit = <T extends Record<string, unknown>>(
  updates: T,
): T & { last_modified_source: "crm_user" } => ({
  ...updates,
  last_modified_source: "crm_user",
});

/**
 * Thrown when an UPDATE on support_tasks matched no row the caller may write.
 *
 * Root cause (PROD 2026-10-02 22:27 and 2026-10-03 14:22): a support_agent pressed Complete on
 * a task that was no longer theirs — both tasks are `assignment_method = operator_reassigned`
 * and now belong to another agent; the panel was showing a stale copy. The UPDATE policy only
 * lets the ASSIGNED agent or a support admin write a task, so Postgres updated ZERO rows:
 *   - PATCHes without a representation came back 204 and LOOKED saved (the due date), and
 *   - the status PATCH with `select=...&.single()` came back 406 (PGRST116, 0 rows).
 * Neither change was written; a supervisor completed both tasks minutes later. So the 406 was
 * a REAL failure, not a cosmetic one. Every UI write now asks for `id` back and treats zero rows
 * as "not saved", with a message that says why, instead of a silent 204 or a raw 406.
 */
export const SUPPORT_TASK_NOT_UPDATED_MESSAGE =
  "This task was not changed. Only the agent it is assigned to, or a support admin, can edit it. It may have been reassigned — refresh the list, or ask a support admin.";

export class SupportTaskNotUpdatedError extends Error {
  constructor(message: string = SUPPORT_TASK_NOT_UPDATED_MESSAGE) {
    super(message);
    this.name = "SupportTaskNotUpdatedError";
  }
}

/** UPDATE one support task and prove a row was written (see SupportTaskNotUpdatedError). */
const updateSupportTaskRow = async (
  taskId: UUID,
  updates: Record<string, unknown>,
): Promise<void> => {
  const { data, error } = await supabase
    .from("support_tasks")
    .update(withCrmUserAudit(updates))
    .eq("id", taskId)
    .select("id");
  if (error) {
    throw error;
  }
  if (!data || data.length === 0) {
    throw new SupportTaskNotUpdatedError();
  }
};

/**
 * A pre-read that finds no row the caller may see (PGRST116, or no data) means they cannot
 * change this task either: report it as the same friendly refusal, not a raw PostgREST error.
 */
const refusalIfInvisible = (error: unknown): Error => {
  const code = (error as { code?: string } | null)?.code;
  if (!error || code === "PGRST116") {
    return new SupportTaskNotUpdatedError();
  }
  return error as Error;
};

/** Re-read a task with the agent/contact/escalation embeds after a proven write. */
const fetchTaskWithEscalationEmbeds = async (
  taskId: UUID,
): Promise<SupportTask | null> => {
  const { data, error } = await supabase
    .from("support_tasks")
    .select(
      `
      *,
      agent:support_agents!support_tasks_agent_id_fkey(id, name, email),
      contact:contacts!support_tasks_contact_id_fkey(id, first_name, last_name, email, phone_number),
      escalated_to_agent:support_agents!support_tasks_escalated_to_fkey(id, name, email),
      original_agent:support_agents!support_tasks_original_agent_id_fkey(id, name, email)
    `,
    )
    .eq("id", taskId)
    .single();
  if (error) {
    // The write landed; only the follow-up read failed (e.g. a de-escalating supervisor can
    // no longer see the task). Never report that as "not saved".
    console.warn("Task saved; re-read failed:", error);
    return null;
  }
  return data as SupportTask;
};
import type {
  SupportTask,
  SupportAgent,
  TaskStatus,
  TaskTabKey,
  TaskFilter,
  TaskSortField,
  TaskSortOrder,
  TaskResolutionData,
  SupportTaskImportBatch,
  CSVTaskRow,
  ImportResult,
  ImportError,
  CustomerSnapshot,
  SupportTaskType,
  ResolutionTemplate,
  TaskTypeFilterValue,
} from "../types/supportTasks.types";
import type { UUID } from "../types/common.types";

/**
 * Priority bucket for task list display order. Operator policy
 * (2026-06-02): tasks whose task_type contains "fortegra" OR "lockton"
 * (case-insensitive) appear at the top of every task list, regardless
 * of the user's selected sort. EXCLUDES "non-fortegra" / "non fortegra"
 * variants — those go in the standard bucket.
 *
 * Returns 0 for priority bucket, 1 for standard. Applied as a stable
 * client-side resort after the PostgREST query so the user's chosen
 * sort still orders WITHIN each bucket.
 */
const taskTypePriorityBucket = (taskType: string | null | undefined): 0 | 1 => {
  if (!taskType) {
    return 1;
  }
  const t = taskType.toLowerCase();
  // Exclude "non-fortegra" / "non fortegra" / "nonfortegra" variants.
  if (
    t.includes("non_fortegra") ||
    t.includes("non-fortegra") ||
    t.includes("nonfortegra") ||
    t.includes("non fortegra")
  ) {
    return 1;
  }
  if (t.includes("fortegra") || t.includes("lockton")) {
    return 0;
  }
  return 1;
};

const applyPriorityBucketSort = <
  T extends { task_type: string | null | undefined },
>(
  tasks: T[],
): T[] => {
  // Stable sort by bucket only — relies on the runtime's Array.sort
  // being stable (true in modern Node + V8) to preserve the upstream
  // PostgREST order WITHIN each bucket.
  return [...tasks].sort(
    (a, b) =>
      taskTypePriorityBucket(a.task_type) - taskTypePriorityBucket(b.task_type),
  );
};

/**
 * 2026-06-09: single source of truth for Support Tasks counts.
 *
 * Returns the same shape the RPC produces. Callers use this for the
 * sidebar agent list, tab badges, unassigned counter, and analytics
 * header — all from one query so the numbers never diverge.
 *
 * Filters mirror the task list filters so counts always describe
 * "the tasks currently visible." Pass NULL/undefined for unfiltered
 * lifetime totals.
 *
 * Replaces the legacy client-side count math in getAgents() which
 * issued an unbounded `.select('agent_id, status, due_date')` and
 * silently truncated at PostgREST's 1,000-row default cap. PROD had
 * 4,156 tasks; every agent was undercounted.
 */
export interface SupportTaskCountsByAgent {
  agent_id: UUID;
  pending_count: number;
  in_progress_count: number;
  past_due_count: number;
  completed_count: number;
  cancelled_count: number;
}

export interface SupportTaskCounts {
  total_open: number;
  total_pending: number;
  total_in_progress: number;
  past_due_open: number;
  unassigned_open: number;
  unassigned_pending: number;
  unassigned_in_progress: number;
  unassigned_past_due: number;
  by_agent: SupportTaskCountsByAgent[];
}

export interface SupportTaskCountsFilters {
  taskType?: string | null;
  search?: string | null;
  tags?: string[] | null;
}

const fetchSupportTaskCounts = async (
  taskType: string | null,
  filters?: SupportTaskCountsFilters,
): Promise<SupportTaskCounts> => {
  const { data, error } = await supabase.rpc("get_support_task_counts", {
    p_task_type: taskType,
    p_search: filters?.search ?? null,
    p_tags: filters?.tags && filters.tags.length > 0 ? filters.tags : null,
  });
  if (error) {
    console.error("Error fetching support task counts:", error);
    throw error;
  }
  return data as SupportTaskCounts;
};

/**
 * Combine per-type get_support_task_counts results into the counts for their UNION (a task-type
 * family filter, 2026-10-16). The types are disjoint, so the OPEN buckets (pending, in progress,
 * past due, unassigned, totals) add up. completed_count / cancelled_count are NOT summed: the RPC
 * returns them as lifetime per-agent totals that ignore p_task_type (see its per_agent_done CTE),
 * so every part carries the same value and summing would multiply them by the number of types.
 */
export const mergeSupportTaskCounts = (
  parts: SupportTaskCounts[],
): SupportTaskCounts => {
  const sum = (k: Exclude<keyof SupportTaskCounts, "by_agent">) =>
    parts.reduce((n, p) => n + (p[k] ?? 0), 0);
  const byAgent = new Map<string, SupportTaskCountsByAgent>();
  for (const part of parts) {
    for (const row of part.by_agent ?? []) {
      const prev = byAgent.get(row.agent_id);
      if (!prev) {
        byAgent.set(row.agent_id, { ...row });
        continue;
      }
      prev.pending_count += row.pending_count;
      prev.in_progress_count += row.in_progress_count;
      prev.past_due_count += row.past_due_count;
      // Lifetime, filter-independent: identical in every part. Keep one, never add.
      prev.completed_count = Math.max(
        prev.completed_count,
        row.completed_count,
      );
      prev.cancelled_count = Math.max(
        prev.cancelled_count,
        row.cancelled_count,
      );
    }
  }
  return {
    total_open: sum("total_open"),
    total_pending: sum("total_pending"),
    total_in_progress: sum("total_in_progress"),
    past_due_open: sum("past_due_open"),
    unassigned_open: sum("unassigned_open"),
    unassigned_pending: sum("unassigned_pending"),
    unassigned_in_progress: sum("unassigned_in_progress"),
    unassigned_past_due: sum("unassigned_past_due"),
    // Same order the RPC uses (ORDER BY agent_id).
    by_agent: [...byAgent.values()].sort((a, b) =>
      a.agent_id.localeCompare(b.agent_id),
    ),
  };
};

export const getSupportTaskCounts = async (
  filters?: SupportTaskCountsFilters,
): Promise<SupportTaskCounts> => {
  // The RPC takes ONE p_task_type. A family filter ("Hard Decline - All") is one call per member
  // type, merged — no database change, and each call is the same audited RPC.
  const types = resolveTaskTypeFilter(filters?.taskType);
  if (!types || types.length === 1) {
    return fetchSupportTaskCounts(types ? types[0] : null, filters);
  }
  const parts = await Promise.all(
    types.map((t) => fetchSupportTaskCounts(t, filters)),
  );
  return mergeSupportTaskCounts(parts);
};

/**
 * Get all active agents, joined with task counts from
 * get_support_task_counts(). One RPC call drives the count math —
 * avoids the prior client-side truncation bug.
 */
export const getAgents = async (): Promise<SupportAgent[]> => {
  // First get ALL active agents from support_agents table
  const { data: agents, error: agentsError } = await supabase
    .from("support_agents")
    .select("id, name, email")
    .eq("is_active", true)
    .order("name");

  if (agentsError) {
    console.error("Error fetching agents:", agentsError);
    throw agentsError;
  }

  if (!agents || agents.length === 0) {
    return [];
  }

  // 2026-06-09: counts come from the get_support_task_counts RPC so we
  // never hit the PostgREST 1,000-row truncation that the legacy
  // client-side scan suffered (PROD 4,156 tasks → every agent under-
  // counted). Filters are intentionally left NULL here — the agent
  // list shows lifetime totals; filter-aware counts come via a
  // separate call to getSupportTaskCounts(filters) on the page.
  const counts = await getSupportTaskCounts();
  const countsByAgent = new Map<UUID, SupportTaskCountsByAgent>();
  for (const row of counts.by_agent) {
    countsByAgent.set(row.agent_id, row);
  }

  return agents.map((agent) => {
    const row = countsByAgent.get(agent.id);
    return {
      id: agent.id,
      name: agent.name,
      email: agent.email || "",
      pending_count: row?.pending_count ?? 0,
      in_progress_count: row?.in_progress_count ?? 0,
      completed_count: row?.completed_count ?? 0,
      cancelled_count: row?.cancelled_count ?? 0,
      past_due_count: row?.past_due_count ?? 0,
    };
  });
};

/**
 * Which tasks a Support Tasks view covers. Exactly one applies, in this precedence:
 *   escalatedTo     -> tasks escalated to that agent ("Escalated to me", 2026-10-03)
 *   unassignedOnly  -> agent_id IS NULL
 *   agentId         -> that agent's tasks
 *   none            -> every task the caller can read (admin "All Agents")
 */
export interface SupportTaskScope {
  agentId?: UUID | null;
  unassignedOnly?: boolean;
  escalatedTo?: UUID | null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const applySupportTaskScope = <Q extends { eq: any; is: any }>(
  query: Q,
  scope: SupportTaskScope,
): Q => {
  if (scope.escalatedTo) {
    return query.eq("escalated_to", scope.escalatedTo);
  }
  if (scope.unassignedOnly) {
    return query.is("agent_id", null);
  }
  if (scope.agentId) {
    return query.eq("agent_id", scope.agentId);
  }
  return query;
};

/**
 * Apply a task-type FILTER value (wire type, family or "all") to a support_tasks query.
 * One type keeps the original `.eq`; a family (e.g. "Hard Decline - All", 2026-10-16) becomes
 * `.in` over its member types. Every list, count and select-all path goes through here so they
 * all describe the same set of tasks.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const applyTaskTypeFilter = <Q extends { eq: any; in: any }>(
  query: Q,
  value: string | null | undefined,
): Q => {
  const types = resolveTaskTypeFilter(value);
  if (!types) {
    return query;
  }
  return types.length === 1
    ? query.eq("task_type", types[0])
    : query.in("task_type", types);
};

const COUNTABLE_COLUMNS =
  "id, status, due_date, agent_id, external_task_id, customer_snapshot, tags, agent:support_agents!support_tasks_agent_id_fkey(name)";

/**
 * Counts for every tab of the current view, loaded up front (2026-10-03: only the SELECTED tab had
 * a real number for support agents). Same scope, task-type filter and client-side filters (search,
 * tag, due today) as the lists, so each badge equals what its tab lists. Reads run as the caller,
 * so RLS decides what is counted exactly as it decides what is listed.
 */
export const getSupportTaskTabCounts = async (
  scope: SupportTaskScope,
  filter: TaskFilter & { tag?: string | null; dueTodayOnly?: boolean },
): Promise<SupportTaskTabCounts> => {
  const taskType = filter.taskType;
  const clientFiltered = !!(
    (filter.searchQuery ?? "").trim() ||
    filter.tag ||
    filter.dueTodayOnly
  );

  const scoped = (statuses: string[]) =>
    fetchAllRows<CountableTask>((from, to) => {
      let q = supabase
        .from("support_tasks")
        .select(COUNTABLE_COLUMNS)
        .in("status", statuses);
      q = applySupportTaskScope(q, scope);
      q = applyTaskTypeFilter(q, taskType);
      return q.order("id").range(from, to) as unknown as PromiseLike<{
        data: CountableTask[] | null;
        error: unknown;
      }>;
    });

  const unassignedRows = fetchAllRows<CountableTask>((from, to) => {
    let q = supabase
      .from("support_tasks")
      .select(COUNTABLE_COLUMNS)
      .is("agent_id", null)
      .in("status", ["pending", "in_progress"]);
    q = applyTaskTypeFilter(q, taskType);
    return q.order("id").range(from, to) as unknown as PromiseLike<{
      data: CountableTask[] | null;
      error: unknown;
    }>;
  });

  // Completed can be large: with no client-side filter an exact head count is enough.
  const completedHead = async (): Promise<number> => {
    let q = supabase
      .from("support_tasks")
      .select("id", { count: "exact", head: true })
      .eq("status", "completed");
    q = applySupportTaskScope(q, scope);
    q = applyTaskTypeFilter(q, taskType);
    const { count, error } = await q;
    if (error) {
      throw error;
    }
    return count ?? 0;
  };

  const [open, unassigned, completed] = await Promise.all([
    scoped(
      clientFiltered
        ? ["pending", "in_progress", "completed"]
        : ["pending", "in_progress"],
    ),
    unassignedRows,
    clientFiltered ? Promise.resolve(null) : completedHead(),
  ]);

  return bucketSupportTaskTabCounts(
    open,
    unassigned,
    {
      searchQuery: filter.searchQuery,
      tag: filter.tag,
      dueTodayOnly: filter.dueTodayOnly,
    },
    completed,
  );
};

/**
 * Get past due tasks for a specific agent
 * Past due = (pending or in_progress) AND due_date < today
 * If agentId is undefined/null, returns past due tasks for ALL agents
 */
export const getPastDueTasks = async (
  agentId: UUID | undefined | null,
  filter?: TaskFilter,
  sortField: TaskSortField = "due_date",
  sortOrder: TaskSortOrder = "asc",
  unassignedOnly: boolean = false,
  escalatedTo: UUID | null = null,
): Promise<SupportTask[]> => {
  // Same cutoff the tab counts use (utils/supportTaskTabCounts).
  const todayISO = pastDueCutoffISO();

  let query = supabase
    .from("support_tasks")
    .select(
      `
      *,
      agent:support_agents!support_tasks_agent_id_fkey(id, name, email),
      contact:contacts!support_tasks_contact_id_fkey(id, first_name, last_name, email, phone_number, organization_id)
    `,
    )
    .in("status", ["pending", "in_progress"])
    .lt("due_date", todayISO);

  query = applySupportTaskScope(query, {
    agentId,
    unassignedOnly,
    escalatedTo,
  });

  // Apply task type filter
  query = applyTaskTypeFilter(query, filter?.taskType);

  // Apply sorting
  const ascending = sortOrder === "asc";
  switch (sortField) {
    case "due_date":
      query = query.order("due_date", { ascending });
      break;
    case "customer_name":
      query = query.order("customer_snapshot->name", { ascending });
      break;
    case "task_type":
      query = query.order("task_type", { ascending });
      break;
    default:
      query = query.order("due_date", { ascending });
  }

  const { data, error } = await query;

  if (error) {
    console.error("Error fetching past due tasks:", error);
    throw error;
  }

  let tasks = data || [];

  // Apply search filter (client-side for flexibility with JSONB)
  if (filter?.searchQuery) {
    tasks = tasks.filter((task) =>
      matchesSupportTaskSearch(task, filter.searchQuery!),
    );
  }

  return applyPriorityBucketSort(tasks);
};

/**
 * Get tasks for a specific agent by status
 * If agentId is undefined/null, returns tasks for ALL agents
 */
/**
 * Get unassigned tasks (agent_id IS NULL) across the active statuses
 * (pending + in_progress). Backs the Unassigned tab on the Support
 * Tasks overview — leadership uses this view to triage and distribute
 * orphan tasks. Mirrors getPastDueTasks in shape.
 *
 * status filter is ignored intentionally: completed/cancelled tasks
 * that happen to have no agent_id aren't actionable triage candidates.
 */
export const getUnassignedTasks = async (
  filter?: TaskFilter,
  sortField: TaskSortField = "due_date",
  sortOrder: TaskSortOrder = "asc",
): Promise<SupportTask[]> => {
  let query = supabase
    .from("support_tasks")
    .select(
      `
      *,
      agent:support_agents!support_tasks_agent_id_fkey(id, name, email),
      contact:contacts!support_tasks_contact_id_fkey(id, first_name, last_name, email, phone_number, organization_id)
    `,
    )
    .is("agent_id", null)
    .in("status", ["pending", "in_progress"]);

  query = applyTaskTypeFilter(query, filter?.taskType);

  const ascending = sortOrder === "asc";
  switch (sortField) {
    case "due_date":
      query = query.order("due_date", { ascending });
      break;
    case "customer_name":
      query = query.order("customer_snapshot->name", { ascending });
      break;
    case "task_type":
      query = query.order("task_type", { ascending });
      break;
    default:
      query = query.order("due_date", { ascending });
  }

  const { data, error } = await query;

  if (error) {
    console.error("Error fetching unassigned tasks:", error);
    throw error;
  }

  let tasks = data || [];

  if (filter?.searchQuery) {
    tasks = tasks.filter((task) =>
      matchesSupportTaskSearch(task, filter.searchQuery!),
    );
  }

  return applyPriorityBucketSort(tasks);
};

/**
 * Count of unassigned active tasks for the tab badge. Cheap head request.
 */
export const getUnassignedTaskCount = async (): Promise<number> => {
  const { count, error } = await supabase
    .from("support_tasks")
    .select("id", { count: "exact", head: true })
    .is("agent_id", null)
    .in("status", ["pending", "in_progress"]);

  if (error) {
    console.error("Error fetching unassigned task count:", error);
    return 0;
  }
  return count ?? 0;
};

/**
 * Tasks for an agent, optionally scoped to one status.
 *
 * ⭐ `status: null` means EVERY status. Reported 2026-09-23: searching for a task required first
 * guessing which tab it lived in (Past Due / Pending / In Progress / Completed / Unassigned),
 * because the search is applied CLIENT-SIDE to whatever the tab had already loaded. A completed
 * task was therefore invisible from the Pending tab no matter what you typed. Passing null lets
 * the caller search across all statuses while keeping every other filter, sort and the same
 * client-side matcher.
 */
export const getAgentTasks = async (
  agentId: UUID | undefined | null,
  status: TaskStatus | null,
  filter?: TaskFilter,
  sortField: TaskSortField = "due_date",
  sortOrder: TaskSortOrder = "asc",
  unassignedOnly: boolean = false,
  escalatedTo: UUID | null = null,
): Promise<SupportTask[]> => {
  let query = supabase.from("support_tasks").select(
    `
      *,
      agent:support_agents!support_tasks_agent_id_fkey(id, name, email),
      contact:contacts!support_tasks_contact_id_fkey(id, first_name, last_name, email, phone_number, organization_id)
    `,
  );

  // null = search every status (see the doc comment above).
  if (status !== null) {
    query = query.eq("status", status);
  }

  // Filter by agent (XOR with unassignedOnly — UI guarantees only one is set).
  // Unassigned filter takes precedence: when both happen to be set,
  // unassigned wins so a stale agentId doesn't accidentally suppress
  // unassigned rows.
  query = applySupportTaskScope(query, {
    agentId,
    unassignedOnly,
    escalatedTo,
  });

  // Apply task type filter
  query = applyTaskTypeFilter(query, filter?.taskType);

  // Apply sorting
  const ascending = sortOrder === "asc";
  switch (sortField) {
    case "due_date":
      query = query.order("due_date", { ascending });
      break;
    case "customer_name":
      // Sort by customer name in snapshot
      query = query.order("customer_snapshot->name", { ascending });
      break;
    case "task_type":
      query = query.order("task_type", { ascending });
      break;
    default:
      query = query.order("due_date", { ascending });
  }

  const { data, error } = await query;

  if (error) {
    console.error("Error fetching agent tasks:", error);
    throw error;
  }

  let tasks = data || [];

  // Apply search filter (client-side for flexibility with JSONB)
  if (filter?.searchQuery) {
    tasks = tasks.filter((task) =>
      matchesSupportTaskSearch(task, filter.searchQuery!),
    );
  }

  return applyPriorityBucketSort(tasks);
};

/**
 * Get a single task by ID
 */
export const getTaskById = async (
  taskId: UUID,
): Promise<SupportTask | null> => {
  const { data, error } = await supabase
    .from("support_tasks")
    .select(
      `
      *,
      agent:support_agents!support_tasks_agent_id_fkey(id, name, email),
      contact:contacts!support_tasks_contact_id_fkey(id, first_name, last_name, email, phone_number, organization_id)
    `,
    )
    .eq("id", taskId)
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      return null; // Not found
    }
    console.error("Error fetching task:", error);
    throw error;
  }

  return data;
};

export const getChildTasks = async (
  parentTaskId: UUID,
): Promise<SupportTask[]> => {
  const { data, error } = await supabase
    .from("support_tasks")
    .select(
      `*, agent:support_agents!support_tasks_agent_id_fkey(id, name, email),
       contact:contacts!support_tasks_contact_id_fkey(id, first_name, last_name, email, phone_number)`,
    )
    .eq("parent_task_id", parentTaskId)
    .order("created_at", { ascending: true });

  if (error) {
    throw error;
  }
  return applyPriorityBucketSort(data || []);
};

/**
 * Update task status with optional resolution data
 */
/**
 * Update a task's priority (low / medium / high / urgent).
 * Server-side CHECK constraint validates; this just routes the value.
 */
export const updateTaskPriority = async (
  taskId: UUID,
  newPriority: "low" | "medium" | "high" | "urgent",
): Promise<SupportTask | null> => {
  await updateSupportTaskRow(taskId, { priority: newPriority });
  return getTaskById(taskId);
};

export const updateTaskStatus = async (
  taskId: UUID,
  newStatus: TaskStatus,
  resolutionData?: TaskResolutionData,
): Promise<SupportTask | null> => {
  const updates: Record<string, unknown> = {
    status: newStatus,
  };

  // Set timestamps based on status
  if (newStatus === "in_progress") {
    // Only set opened_at if not already set
    const existing = await getTaskById(taskId);
    if (existing && !existing.opened_at) {
      updates.opened_at = new Date().toISOString();
    }
  }

  if (newStatus === "completed" || newStatus === "cancelled") {
    updates.completed_at = new Date().toISOString();
    if (resolutionData?.notes) {
      updates.notes = resolutionData.notes;
    }
    if (resolutionData?.tags) {
      updates.tags = resolutionData.tags;
    }
  }

  // Clear resolution data when moving back to pending/in_progress
  if (newStatus === "pending" || newStatus === "in_progress") {
    const existing = await getTaskById(taskId);
    if (
      existing &&
      (existing.status === "completed" || existing.status === "cancelled")
    ) {
      updates.completed_at = null;
    }
  }

  try {
    await updateSupportTaskRow(taskId, updates);
  } catch (error) {
    console.error("Error updating task status:", error);
    throw error;
  }
  // Refetch with the embeds AFTER the write is proven, instead of asking the UPDATE for them.
  return getTaskById(taskId);
};

/**
 * Append a note to a task's notes (the "Add note" path: Log and Update with no status change).
 * Appends rather than replaces, so API-originated notes are kept. Writes only `notes`, which
 * migration 200 also allows for the supervisor a task is escalated to.
 */
export const appendTaskNote = async (
  taskId: UUID,
  note: string,
  author: string,
  now: Date = new Date(),
): Promise<void> => {
  const { data, error } = await supabase
    .from("support_tasks")
    .select("notes")
    .eq("id", taskId)
    .maybeSingle();
  if (error) {
    throw error;
  }
  if (!data) {
    throw new SupportTaskNotUpdatedError();
  }
  // Local calendar date (en-CA = YYYY-MM-DD), so an evening note isn't stamped tomorrow (UTC).
  const day = now.toLocaleDateString("en-CA");
  const stamp = `[${day}${author ? ` · ${author}` : ""}]`;
  const existing = (data.notes ?? "").trim();
  const notes = existing
    ? `${existing}\n\n${stamp} ${note}`
    : `${stamp} ${note}`;
  await updateSupportTaskRow(taskId, { notes });
};

/**
 * Mark task as opened (set to in_progress if pending)
 */
export const markTaskOpened = async (
  taskId: UUID,
): Promise<SupportTask | null> => {
  const task = await getTaskById(taskId);
  if (!task) {
    return null;
  }

  if (task.status === "pending") {
    return updateTaskStatus(taskId, "in_progress");
  }

  // If already in_progress or other status, just return the task
  return task;
};

/**
 * Mark task as viewed — sets opened_at without changing status.
 * Only updates if opened_at is currently null (first view).
 */
export const markTaskViewed = async (taskId: UUID): Promise<void> => {
  await supabase
    .from("support_tasks")
    .update(withCrmUserAudit({ opened_at: new Date().toISOString() }))
    .eq("id", taskId)
    .is("opened_at", null);
};

/**
 * Update a task's due date
 */
export const updateTaskDueDate = async (
  taskId: UUID,
  dueDate: string,
): Promise<void> => {
  try {
    await updateSupportTaskRow(taskId, { due_date: dueDate });
  } catch (error) {
    console.error("Error updating task due date:", error);
    throw error;
  }
};

/**
 * Update a task's type
 */
export const updateTaskType = async (
  taskId: UUID,
  taskType: SupportTaskType,
): Promise<void> => {
  try {
    await updateSupportTaskRow(taskId, { task_type: taskType });
  } catch (error) {
    console.error("Error updating task type:", error);
    throw error;
  }
};

/**
 * Link an interaction to a task
 * Sets support_task_id on the interaction (for querying interactions by task)
 * Also updates interaction_id on the task (for backward compatibility)
 */
export const linkInteractionToTask = async (
  taskId: UUID,
  interactionId: UUID,
): Promise<SupportTask | null> => {
  // Set support_task_id on the interaction (for getLinkedInteractions)
  const { error: interactionError } = await supabase
    .from("interactions")
    .update({ support_task_id: taskId })
    .eq("id", interactionId);

  if (interactionError) {
    console.error(
      "Error setting support_task_id on interaction:",
      interactionError,
    );
    // Don't throw - continue with task update for backward compatibility
  }

  // Also update interaction_id on the task (for backward compatibility)
  try {
    await updateSupportTaskRow(taskId, { interaction_id: interactionId });
  } catch (error) {
    console.error("Error linking interaction to task:", error);
    throw error;
  }

  return getTaskById(taskId);
};

/**
 * Update customer tags in the snapshot
 */
export const updateCustomerTags = async (
  taskId: UUID,
  tags: string[],
): Promise<SupportTask | null> => {
  const task = await getTaskById(taskId);
  if (!task) {
    return null;
  }

  const updatedSnapshot = {
    ...task.customer_snapshot,
    tags,
  };

  try {
    await updateSupportTaskRow(taskId, { customer_snapshot: updatedSnapshot });
  } catch (error) {
    console.error("Error updating customer tags:", error);
    throw error;
  }

  return getTaskById(taskId);
};

/**
 * Add a tag to customer snapshot
 */
export const addCustomerTag = async (
  taskId: UUID,
  tag: string,
): Promise<SupportTask | null> => {
  const task = await getTaskById(taskId);
  if (!task) {
    return null;
  }

  const currentTags = task.customer_snapshot.tags || [];
  if (currentTags.includes(tag)) {
    return task; // Tag already exists
  }

  return updateCustomerTags(taskId, [...currentTags, tag]);
};

/**
 * Remove a tag from customer snapshot
 */
export const removeCustomerTag = async (
  taskId: UUID,
  tag: string,
): Promise<SupportTask | null> => {
  const task = await getTaskById(taskId);
  if (!task) {
    return null;
  }

  const currentTags = task.customer_snapshot.tags || [];
  return updateCustomerTags(
    taskId,
    currentTags.filter((t: string) => t !== tag),
  );
};

// ============================================================================
// CSV Import Functions
// ============================================================================

// Cache for U.S. LawShield organization ID (fetched once per session)
let uslsOrgIdCache: UUID | null = null;

/**
 * Get the U.S. LawShield organization ID
 * All support task contacts are assigned to this organization
 */
const getUSLSOrganizationId = async (): Promise<UUID | null> => {
  if (uslsOrgIdCache) {
    return uslsOrgIdCache;
  }

  const { data, error } = await supabase
    .from("organizations")
    .select("id")
    // System-internal sentinel lookup — resolve the "U.S. LawShield" org for support-task routing
    // REGARDLESS of archived state. It should never be disabled, but if it were, support-task
    // resolution must not silently break. (Do not add an is_archived filter here.)
    .eq("name", "U.S. LawShield")
    .maybeSingle();

  if (error) {
    console.error("Error fetching U.S. LawShield organization:", error);
    return null;
  }

  if (data) {
    uslsOrgIdCache = data.id;
  }

  return uslsOrgIdCache;
};

/**
 * Create a contact_organizations junction record
 */
const createContactOrganizationLink = async (
  contactId: UUID,
  organizationId: UUID,
  relationshipType: string = "member",
): Promise<void> => {
  const { error } = await supabase.from("contact_organizations").upsert(
    {
      contact_id: contactId,
      organization_id: organizationId,
      relationship_type: relationshipType,
      is_primary: true,
      source: "csv_import",
    },
    {
      onConflict: "contact_id,organization_id",
    },
  );

  if (error) {
    console.error("Error creating contact_organizations link:", error);
    // Don't throw - this is supplementary data
  }
};

/**
 * Find or create an agent profile by name
 */
const findOrCreateAgent = async (
  agentName: string,
  agentEmail?: string,
  externalAgentId?: string,
): Promise<{ id: UUID; created: boolean }> => {
  // First try to find by external_agent_id if provided
  if (externalAgentId) {
    const { data: byExternalId } = await supabase
      .from("support_agents")
      .select("id")
      .eq("external_agent_id", externalAgentId)
      .maybeSingle();

    if (byExternalId) {
      return { id: byExternalId.id, created: false };
    }
  }

  // Try to find by name in support_agents table
  const { data: existing } = await supabase
    .from("support_agents")
    .select("id")
    .eq("name", agentName)
    .maybeSingle();

  if (existing) {
    // Update external_agent_id if not set and we have one
    if (externalAgentId) {
      await supabase
        .from("support_agents")
        .update({ external_agent_id: externalAgentId })
        .eq("id", existing.id);
    }
    return { id: existing.id, created: false };
  }

  // Create new agent in support_agents table
  const { data: newAgent, error } = await supabase
    .from("support_agents")
    .insert({
      name: agentName,
      email: agentEmail || null,
      external_agent_id: externalAgentId || null,
      is_active: true,
    })
    .select("id")
    .single();

  if (error) {
    console.error("Error creating agent:", error);
    throw error;
  }

  return { id: newAgent.id, created: true };
};

/**
 * Find or create a contact by Zuora GUID or email
 * All contacts are assigned to the U.S. LawShield organization
 */
const findOrCreateContact = async (
  row: CSVTaskRow,
): Promise<{ id: UUID; created: boolean }> => {
  const zuoraGuid = row["Zuora Account GUID"];
  const email = row.Email?.trim();

  // Get U.S. LawShield organization ID for all imported contacts
  const uslsOrgId = await getUSLSOrganizationId();

  // Try to find by Zuora GUID first
  if (zuoraGuid) {
    const { data: byGuid } = await supabase
      .from("contacts")
      .select("id, organization_id")
      .eq("zuora_account_id", zuoraGuid)
      .maybeSingle();

    if (byGuid) {
      // Update contact with latest data
      await updateContactFromRow(byGuid.id, row, uslsOrgId);
      // Ensure contact_organizations link exists
      if (uslsOrgId) {
        await createContactOrganizationLink(byGuid.id, uslsOrgId, "member");
      }
      return { id: byGuid.id, created: false };
    }
  }

  // Try to find by email
  if (email) {
    const { data: byEmail } = await supabase
      .from("contacts")
      .select("id, organization_id")
      .eq("email", email)
      .maybeSingle();

    if (byEmail) {
      // Update contact with latest data (including Zuora GUID)
      await updateContactFromRow(byEmail.id, row, uslsOrgId);
      // Ensure contact_organizations link exists
      if (uslsOrgId) {
        await createContactOrganizationLink(byEmail.id, uslsOrgId, "member");
      }
      return { id: byEmail.id, created: false };
    }
  }

  // Create new contact with U.S. LawShield organization
  const nameParts = row["Account Name"].trim().split(/\s+/);
  const firstName = nameParts[0] || "";
  const lastName = nameParts.slice(1).join(" ") || "";

  // SMS plan §4.3: a busy number gate aborts the insert with nothing written, so the identical
  // insert is re-run (bounded). Any other error, including a network error, is NOT retried.
  const { data: newContact, error } = await retryOnSmsNumberBusy(() =>
    supabase
      .from("contacts")
      .insert({
        first_name: firstName,
        last_name: lastName,
        email: email || null,
        phone_number: row["Mobile Phone"]?.trim() || null,
        zuora_account_id: zuoraGuid || null,
        address_line1: row.Address1?.trim() || null,
        address_line2: row.Address2?.trim() || null,
        city: row.City?.trim() || null,
        state: row.State?.trim() || null,
        postal_code: row["Postal Code"]?.trim() || null,
        source: "csv_import",
        is_usls_member: true,
        member_status: row["Account Status"]?.trim() || null,
        organization_id: uslsOrgId || null,
      })
      .select("id")
      .single(),
  );

  if (error) {
    console.error("Error creating contact:", error);
    throw error;
  }

  // Create contact_organizations junction record
  if (uslsOrgId) {
    await createContactOrganizationLink(newContact.id, uslsOrgId, "member");
  }

  return { id: newContact.id, created: true };
};

/**
 * Update an existing contact with data from CSV row
 */
const updateContactFromRow = async (
  contactId: UUID,
  row: CSVTaskRow,
  organizationId?: UUID | null,
): Promise<void> => {
  const updates: Record<string, unknown> = {};

  // Update Zuora GUID if provided
  if (row["Zuora Account GUID"]) {
    updates.zuora_account_id = row["Zuora Account GUID"];
  }

  // Update phone if provided
  if (row["Mobile Phone"]?.trim()) {
    updates.phone_number = row["Mobile Phone"].trim();
  }

  // Update address if provided
  if (row.Address1?.trim()) {
    updates.address_line1 = row.Address1.trim();
  }
  if (row.Address2?.trim()) {
    updates.address_line2 = row.Address2.trim();
  }
  if (row.City?.trim()) {
    updates.city = row.City.trim();
  }
  if (row.State?.trim()) {
    updates.state = row.State.trim();
  }
  if (row["Postal Code"]?.trim()) {
    updates.postal_code = row["Postal Code"].trim();
  }

  // Update member status
  if (row["Account Status"]?.trim()) {
    updates.member_status = row["Account Status"].trim();
  }

  // Set organization_id if contact doesn't have one and we have a value
  if (organizationId) {
    updates.organization_id = organizationId;
  }

  if (Object.keys(updates).length > 0) {
    // SMS plan §4.3: bounded re-run of the same update (keyed on id) on SMS_NUMBER_BUSY only. An
    // exhausted busy refusal fails the ROW (shown to the importer as a retry-in-a-moment message)
    // rather than silently dropping the phone change; every other error is still ignored, as before.
    const { error } = await retryOnSmsNumberBusy(() =>
      supabase.from("contacts").update(updates).eq("id", contactId),
    );
    if (isSmsNumberBusy(error)) {
      throw error;
    }
  }
};

/**
 * Create or update a support task from CSV row
 */
const createOrUpdateTask = async (
  row: CSVTaskRow,
  agentId: UUID,
  contactId: UUID,
  batchId: UUID,
  taskType: SupportTaskType = "28_day_past_due_legacy_retention",
): Promise<{ created: boolean }> => {
  const invoiceNumber = row.InvoiceNumber?.trim();

  // Build customer snapshot
  const addressParts = [row.Address1?.trim()];
  if (row.Address2?.trim()) {
    addressParts.push(row.Address2.trim());
  }
  addressParts.push(
    `${row.City?.trim() || ""}, ${row.State?.trim() || ""} ${row["Postal Code"]?.trim() || ""}`.trim(),
  );

  const customerSnapshot: CustomerSnapshot = {
    id: row["Zuora Account GUID"],
    name: row["Account Name"].trim(),
    email: row.Email?.trim(),
    phone: row["Mobile Phone"]?.trim(),
    member_id: row["Account Number"]?.trim(),
    plan_type: row["Product Name"]?.trim(),
    account_status: row["Account Status"]?.trim(),
    days_past_due: parseInt(row["Invoice Days"], 10) || 28,
    account_balance: formatCurrency(row["Account Balance"]),
    invoice_balance: formatCurrency(row["Invoice Balance"]),
    invoice_amount: formatCurrency(row["Invoice Amount"]),
    invoice_number: invoiceNumber,
    address: addressParts.filter(Boolean).join(", "),
    region: row.Region?.trim(),
  };

  // Parse due date
  let dueDate: string;
  try {
    const [month, day, year] = row["Due Date"].split("/");
    dueDate = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  } catch {
    dueDate = new Date().toISOString().split("T")[0];
  }

  // Check if task already exists for this invoice
  if (invoiceNumber) {
    const { data: existing } = await supabase
      .from("support_tasks")
      .select("id, status")
      .eq("source_invoice_number", invoiceNumber)
      .maybeSingle();

    if (existing) {
      // Update existing task (but don't change status if already in progress/completed)
      if (existing.status === "pending") {
        await supabase
          .from("support_tasks")
          .update(
            withCrmUserAudit({
              agent_id: agentId,
              contact_id: contactId,
              customer_snapshot: customerSnapshot,
              due_date: dueDate,
              import_batch_id: batchId,
            }),
          )
          .eq("id", existing.id);
      }
      return { created: false };
    }
  }

  // Create new task
  const { error } = await supabase.from("support_tasks").insert({
    agent_id: agentId,
    contact_id: contactId,
    task_type: taskType,
    status: "pending",
    due_date: dueDate,
    customer_snapshot: customerSnapshot,
    import_batch_id: batchId,
    source_invoice_number: invoiceNumber,
  });

  if (error) {
    console.error("Error creating task:", error);
    throw error;
  }

  return { created: true };
};

/**
 * Format a value as US currency
 */
const formatCurrency = (value: string): string => {
  try {
    const num = parseFloat(value.replace(/[,$]/g, ""));
    if (isNaN(num)) {
      return "$0.00";
    }
    return `$${num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  } catch {
    return "$0.00";
  }
};

/**
 * Process a CSV file and import tasks
 */
export const importTasksFromCSV = async (
  rows: CSVTaskRow[],
  fileName: string,
  fileSize?: number,
  taskType: SupportTaskType = "28_day_past_due_legacy_retention",
): Promise<ImportResult> => {
  const errors: ImportError[] = [];
  let contactsCreated = 0;
  let contactsUpdated = 0;
  let tasksCreated = 0;
  let tasksUpdated = 0;
  let agentsCreated = 0;

  // Create import batch record
  const { data: batch, error: batchError } = await supabase
    .from("support_task_import_batches")
    .insert({
      file_name: fileName,
      file_size_bytes: fileSize,
      total_rows: rows.length,
      status: "processing",
    })
    .select()
    .single();

  if (batchError) {
    console.error("Error creating import batch:", batchError);
    throw batchError;
  }

  const batchId = batch.id;

  // Cache for agents to avoid repeated lookups
  const agentCache = new Map<string, UUID>();

  // Process each row
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const rowNum = i + 2; // +2 for 1-indexed and header row

    try {
      // Validate required fields
      const agentName = row["Agent Name"]?.trim();
      if (!agentName) {
        errors.push({
          row: rowNum,
          field: "Agent Name",
          message: "Agent name is required",
        });
        continue;
      }

      const zuoraGuid = row["Zuora Account GUID"]?.trim();
      const email = row.Email?.trim();
      if (!zuoraGuid && !email) {
        errors.push({
          row: rowNum,
          message: "Either Zuora Account GUID or Email is required",
        });
        continue;
      }

      // Find or create agent
      // Check cache by external ID first, then by name
      let agentId = row.AgentId
        ? agentCache.get(`external:${row.AgentId}`)
        : undefined;
      if (!agentId) {
        agentId = agentCache.get(agentName);
      }
      if (!agentId) {
        const agentResult = await findOrCreateAgent(
          agentName,
          row["Agent Email"],
          row.AgentId,
        );
        agentId = agentResult.id;
        // Cache by both name and external ID if available
        agentCache.set(agentName, agentId);
        if (row.AgentId) {
          agentCache.set(`external:${row.AgentId}`, agentId);
        }
        if (agentResult.created) {
          agentsCreated++;
        }
      }

      // Find or create contact
      const contactResult = await findOrCreateContact(row);
      if (contactResult.created) {
        contactsCreated++;
      } else {
        contactsUpdated++;
      }

      // Create or update task
      const taskResult = await createOrUpdateTask(
        row,
        agentId,
        contactResult.id,
        batchId,
        taskType,
      );
      if (taskResult.created) {
        tasksCreated++;
      } else {
        tasksUpdated++;
      }
    } catch (err) {
      // SMS plan §4.3: an exhausted SMS_NUMBER_BUSY retry is reported in words the importer can
      // act on (re-import the row in a moment); every other error keeps its message.
      let message = err instanceof Error ? err.message : "Unknown error";
      if (isSmsNumberBusy(err)) {
        message = SMS_NUMBER_BUSY_MESSAGE;
      }
      errors.push({ row: rowNum, message });
    }
  }

  // Update batch record with results
  await supabase
    .from("support_task_import_batches")
    .update({
      contacts_created: contactsCreated,
      contacts_updated: contactsUpdated,
      tasks_created: tasksCreated,
      errors: errors.length,
      error_details: errors,
      status: errors.length === rows.length ? "failed" : "completed",
      completed_at: new Date().toISOString(),
    })
    .eq("id", batchId);

  return {
    batch_id: batchId,
    total_rows: rows.length,
    contacts_created: contactsCreated,
    contacts_updated: contactsUpdated,
    tasks_created: tasksCreated,
    tasks_updated: tasksUpdated,
    agents_created: agentsCreated,
    errors,
  };
};

/**
 * Get import batch history
 */
export const getImportBatches = async (
  limit = 20,
): Promise<SupportTaskImportBatch[]> => {
  const { data, error } = await supabase
    .from("support_task_import_batches")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error) {
    console.error("Error fetching import batches:", error);
    throw error;
  }

  return data || [];
};

/**
 * Get tasks for a specific contact
 */
export const getTasksForContact = async (
  contactId: UUID,
): Promise<SupportTask[]> => {
  const { data, error } = await supabase
    .from("support_tasks")
    .select(
      `
      *,
      agent:support_agents!support_tasks_agent_id_fkey(id, name, email)
    `,
    )
    .eq("contact_id", contactId)
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching tasks for contact:", error);
    throw error;
  }

  return applyPriorityBucketSort(data || []);
};

/**
 * Search tasks across all agents
 */
export const searchTasks = async (
  query: string,
  status?: TaskStatus,
): Promise<SupportTask[]> => {
  let dbQuery = supabase.from("support_tasks").select(`
      *,
      agent:support_agents!support_tasks_agent_id_fkey(id, name, email),
      contact:contacts!support_tasks_contact_id_fkey(id, first_name, last_name, email, phone_number, organization_id)
    `);

  if (status) {
    dbQuery = dbQuery.eq("status", status);
  }

  const { data, error } = await dbQuery;

  if (error) {
    console.error("Error searching tasks:", error);
    throw error;
  }

  // Client-side search through customer_snapshot
  return (data || []).filter((task) => matchesSupportTaskSearch(task, query));
};

// ============================================================================
// Support Agent & Admin Functions
// ============================================================================

/**
 * Get agent info for a user by their profile ID
 * Used by Support Agents to find their own agent record
 */
export const getAgentByUserId = async (
  userId: UUID,
): Promise<SupportAgent | null> => {
  // PostgREST self-referencing embeds on support_agents.supervisor_id don't
  // resolve reliably, so fetch the supervisor row separately when needed.
  const { data: agent, error: agentError } = await supabase
    .from("support_agents")
    .select("id, name, email, supervisor_id, is_active")
    .eq("user_id", userId)
    .maybeSingle();

  if (agentError) {
    console.error("Error fetching agent:", agentError);
    return null;
  }

  if (!agent) {
    return null;
  }

  let supervisor: { id: UUID; name: string; email: string } | undefined;
  if (agent.supervisor_id) {
    const { data: supRow } = await supabase
      .from("support_agents")
      .select("id, name, email")
      .eq("id", agent.supervisor_id)
      .maybeSingle();
    if (supRow) {
      supervisor = {
        id: supRow.id,
        name: supRow.name,
        email: supRow.email || "",
      };
    }
  }

  // Get task counts for this agent (including due_date for past_due calculation)
  const { data: tasks, error: tasksError } = await supabase
    .from("support_tasks")
    .select("status, due_date")
    .eq("agent_id", agent.id);

  if (tasksError) {
    console.error("Error fetching agent tasks:", tasksError);
    return null;
  }

  // Get today's date for past due calculation
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Count by status
  const counts = {
    pending: 0,
    in_progress: 0,
    completed: 0,
    cancelled: 0,
    past_due: 0,
  };

  for (const task of tasks || []) {
    if (task.status in counts) {
      counts[task.status as keyof typeof counts]++;
    }
    // Count past due tasks
    if (
      (task.status === "pending" || task.status === "in_progress") &&
      task.due_date
    ) {
      const dueDate = new Date(task.due_date);
      if (dueDate < today) {
        counts.past_due++;
      }
    }
  }

  return {
    id: agent.id,
    name: agent.name,
    email: agent.email || "",
    pending_count: counts.pending,
    in_progress_count: counts.in_progress,
    completed_count: counts.completed,
    cancelled_count: counts.cancelled,
    past_due_count: counts.past_due,
    supervisor_id: agent.supervisor_id,
    supervisor,
    is_active: agent.is_active,
  };
};

/**
 * Reassign a single task to a different agent.
 *
 * Calls reassign_support_task RPC (SECURITY DEFINER) which enforces:
 *   - admin / superadmin / support_admin role gate
 *   - target agent must exist
 *   - task must not be in completed / cancelled status
 * Same RPC the bulk path uses — single + bulk semantics are identical
 * (audit-AI 2026-05-14 must-fix #2).
 */
export const reassignTask = async (
  taskId: UUID,
  newAgentId: UUID,
): Promise<SupportTask | null> => {
  const { error: rpcError } = await supabase.rpc("reassign_support_task", {
    p_task_id: taskId,
    p_new_agent_id: newAgentId,
  });

  if (rpcError) {
    console.error("Error reassigning task:", rpcError);
    throw rpcError;
  }

  // Re-fetch the task row with the joins the UI expects.
  const { data, error: fetchError } = await supabase
    .from("support_tasks")
    .select(
      `
      *,
      agent:support_agents!support_tasks_agent_id_fkey(id, name, email),
      contact:contacts!support_tasks_contact_id_fkey(id, first_name, last_name, email, phone_number, organization_id)
    `,
    )
    .eq("id", taskId)
    .single();

  if (fetchError) {
    console.error("Error refetching task after reassign:", fetchError);
    throw fetchError;
  }
  return data;
};

/**
 * Bulk reassign multiple tasks to a different agent.
 *
 * Calls bulk_reassign_support_tasks RPC (SECURITY DEFINER). Atomic:
 * if any of the supplied task ids are in completed / cancelled status,
 * the entire call raises and no rows are updated.
 *
 * The uuid[] parameter sidesteps the URL-length cap that affected the
 * prior `.in("id", taskIds)` form for large batches (audit-AI must-fix
 * #6).
 */
export const bulkReassignTasks = async (
  taskIds: UUID[],
  newAgentId: UUID,
): Promise<number> => {
  const { data, error } = await supabase.rpc("bulk_reassign_support_tasks", {
    p_task_ids: taskIds,
    p_new_agent_id: newAgentId,
  });

  if (error) {
    console.error("Error bulk reassigning tasks:", error);
    throw error;
  }

  return typeof data === "number" ? data : 0;
};

/**
 * Bulk SOFT-delete support tasks. Restricted to support_admin / admin / superadmin — the RPC
 * enforces that itself and also checks `is_active`, so the UI gate below is a courtesy, not the
 * control.
 *
 * Soft, not hard: the row survives, is hidden from every authenticated reader by a RESTRICTIVE RLS
 * policy, and REMAINS VISIBLE to the service-role API path. That last part is deliberate — a
 * deleted task still suppresses a re-send of the same task type for that member inside the dedup
 * window, so the BI team re-emitting cannot resurrect work that support has finished with.
 *
 * Returns the number actually deleted. Re-deleting an already-deleted task reports 0 rather than
 * re-stamping who deleted it and when.
 *
 * Uses a uuid[] parameter for the same reason bulkReassignTasks does: an `.in("id", ...)` form hits
 * the URL-length cap on large batches.
 */
export const bulkSoftDeleteTasks = async (
  taskIds: UUID[],
  reason?: string,
): Promise<number> => {
  const { data, error } = await supabase.rpc("bulk_soft_delete_support_tasks", {
    p_task_ids: taskIds,
    p_reason: reason ?? null,
  });

  if (error) {
    console.error("Error bulk soft-deleting tasks:", error);
    throw error;
  }

  return typeof data === "number" ? data : 0;
};

/**
 * Return the ids of every task matching the current server-side filters
 * (status / past-due / agent / task type) WITHOUT applying the
 * client-side search predicate. Used by the "Select all N matching" UI
 * affordance — by definition skips searchQuery so we can guarantee the
 * id set the operator selects matches what the bulk-reassign RPC sees.
 * When the operator has a search active, the UI should not offer this
 * affordance (audit-AI 2026-05-14 must-fix #5).
 *
 * Paginates so it never silently truncates above the 1000-row PostgREST
 * cap. Returns id-only rows for minimal payload.
 */
export const getMatchingTaskIds = async (
  agentId: UUID | undefined | null,
  tabKey: TaskTabKey,
  filter?: TaskFilter,
): Promise<UUID[]> => {
  const PAGE_SIZE = 1000;
  const ids: UUID[] = [];
  let offset = 0;
  let hasMore = true;

  while (hasMore) {
    let query = supabase
      .from("support_tasks")
      .select("id")
      .order("id", { ascending: true })
      .range(offset, offset + PAGE_SIZE - 1);

    if (tabKey === "past_due") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayISO = today.toISOString().split("T")[0];
      query = query
        .in("status", ["pending", "in_progress"])
        .lt("due_date", todayISO);
    } else {
      query = query.eq("status", tabKey);
    }

    if (agentId) {
      query = query.eq("agent_id", agentId);
    }
    query = applyTaskTypeFilter(query, filter?.taskType);

    const { data, error } = await query;
    if (error) {
      console.error(`getMatchingTaskIds (offset ${offset}) failed:`, error);
      throw error;
    }
    const page = (data ?? []) as Array<{ id: UUID }>;
    if (page.length === 0) {
      hasMore = false;
    } else {
      for (const row of page) {
        ids.push(row.id);
      }
      if (page.length < PAGE_SIZE) {
        hasMore = false;
      } else {
        offset += PAGE_SIZE;
      }
    }
  }

  return ids;
};

// Pagination bounds for support_tasks dashboard fetches. PAGE_SIZE stays at
// the PostgREST default of 1000; HARD_CAP is a defense-in-depth memory
// backstop. Currently prod has ~3,925 tasks (4 pages) — the unpaginated
// queries were silently truncating to 1000 and misreporting headline counts.
// Mirrors the resolveAudience / loadVoucherIndex pattern in
// supabase/functions/_shared/flightSendHelpers.ts (2026-05-13 audit-AI
// mandate, extended to dashboard service via 2026-05-15 round-2 audit).
const SUPPORT_TASKS_PAGE_SIZE = 1000;
const SUPPORT_TASKS_HARD_CAP = 50_000;

/**
 * Get all unique tags used across tasks for filtering
 */
export const getUniqueTags = async (): Promise<string[]> => {
  const tagSet = new Set<string>();
  let offset = 0;
  let loaded = 0;

  const hasMore = true;
  while (hasMore) {
    const { data, error } = await supabase
      .from("support_tasks")
      .select("id, tags")
      .not("tags", "is", null)
      .order("id", { ascending: true })
      .range(offset, offset + SUPPORT_TASKS_PAGE_SIZE - 1);

    if (error) {
      console.error("Error fetching tags:", error);
      return [];
    }

    const page = data || [];
    if (page.length === 0) {
      break;
    }

    if (loaded + page.length > SUPPORT_TASKS_HARD_CAP) {
      console.error(
        `getUniqueTags: support_tasks exceeds ${SUPPORT_TASKS_HARD_CAP} rows — returning partial tag set`,
      );
      break;
    }

    for (const task of page) {
      if (task.tags && Array.isArray(task.tags)) {
        task.tags.forEach((tag: string) => tagSet.add(tag));
      }
    }

    loaded += page.length;
    if (page.length < SUPPORT_TASKS_PAGE_SIZE) {
      break;
    }
    offset += SUPPORT_TASKS_PAGE_SIZE;
  }

  return Array.from(tagSet).sort();
};

/**
 * Analytics data for Support Admin dashboard
 */
export interface TaskAnalytics {
  date: string;
  new_tasks: number;
  completed: number;
  cancelled: number;
  in_progress: number;
}

export interface TaskAnalyticsFilters {
  startDate?: string;
  endDate?: string;
  taskType?: TaskTypeFilterValue;
  agentId?: UUID;
  resolutionDays?: "1" | "2" | "more";
  tags?: string[];
}

/**
 * Get task analytics data for charting
 */
export const getTaskAnalytics = async (
  filters: TaskAnalyticsFilters,
): Promise<TaskAnalytics[]> => {
  // Build date range
  const endDate = filters.endDate || new Date().toISOString().split("T")[0];
  const startDate =
    filters.startDate ||
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];

  // Paginated fetch of all matching tasks in the date range. Without this the
  // dashboard analytics chart was capped at the PostgREST default of 1000
  // rows — prod is currently at ~3,925 tasks, so all charts were
  // silently truncating to the most recent 1000 (by undefined order) for the
  // last several months.
  const buildAnalyticsQuery = () => {
    let q = supabase
      .from("support_tasks")
      .select("id, status, task_type, agent_id, created_at, completed_at, tags")
      .gte("created_at", startDate)
      .lte("created_at", endDate + "T23:59:59");
    q = applyTaskTypeFilter(q, filters?.taskType);
    if (filters.agentId) {
      q = q.eq("agent_id", filters.agentId);
    }
    return q;
  };

  const allTasks: Array<{
    id: string;
    status: string;
    task_type: string;
    agent_id: string | null;
    created_at: string;
    completed_at: string | null;
    tags: string[] | null;
  }> = [];
  let analyticsOffset = 0;
  const hasMore = true;
  while (hasMore) {
    const { data, error } = await buildAnalyticsQuery()
      .order("id", { ascending: true })
      .range(analyticsOffset, analyticsOffset + SUPPORT_TASKS_PAGE_SIZE - 1);

    if (error) {
      console.error("Error fetching analytics:", error);
      throw error;
    }

    const page = data || [];
    if (page.length === 0) {
      break;
    }
    if (allTasks.length + page.length > SUPPORT_TASKS_HARD_CAP) {
      throw new Error(
        `Support task analytics exceeds ${SUPPORT_TASKS_HARD_CAP} rows in the selected range — narrow the date range or filters before retrying.`,
      );
    }
    allTasks.push(...(page as typeof allTasks));
    if (page.length < SUPPORT_TASKS_PAGE_SIZE) {
      break;
    }
    analyticsOffset += SUPPORT_TASKS_PAGE_SIZE;
  }

  // Filter by resolution time if specified
  let filteredTasks: typeof allTasks = allTasks;
  if (filters.resolutionDays) {
    filteredTasks = filteredTasks.filter((task) => {
      if (!task.completed_at || !task.created_at) {
        return false;
      }
      const created = new Date(task.created_at);
      const completed = new Date(task.completed_at);
      const days = Math.ceil(
        (completed.getTime() - created.getTime()) / (1000 * 60 * 60 * 24),
      );
      switch (filters.resolutionDays) {
        case "1":
          return days <= 1;
        case "2":
          return days === 2;
        case "more":
          return days > 2;
        default:
          return true;
      }
    });
  }

  // Filter by tags if specified
  if (filters.tags && filters.tags.length > 0) {
    filteredTasks = filteredTasks.filter((task) =>
      filters.tags!.some((tag) => task.tags?.includes(tag)),
    );
  }

  // Group by date
  const dateMap = new Map<
    string,
    {
      new_tasks: number;
      completed: number;
      cancelled: number;
      in_progress: number;
    }
  >();

  // Initialize all dates in range
  const current = new Date(startDate);
  const end = new Date(endDate);
  while (current <= end) {
    const dateStr = current.toISOString().split("T")[0];
    dateMap.set(dateStr, {
      new_tasks: 0,
      completed: 0,
      cancelled: 0,
      in_progress: 0,
    });
    current.setDate(current.getDate() + 1);
  }

  // Count tasks by date
  for (const task of filteredTasks) {
    const createdDate = task.created_at.split("T")[0];
    if (dateMap.has(createdDate)) {
      const entry = dateMap.get(createdDate)!;
      entry.new_tasks++;
    }

    if (task.completed_at) {
      const completedDate = task.completed_at.split("T")[0];
      if (dateMap.has(completedDate)) {
        const entry = dateMap.get(completedDate)!;
        if (task.status === "completed") {
          entry.completed++;
        } else if (task.status === "cancelled") {
          entry.cancelled++;
        }
      }
    }
  }

  // Convert to array
  return Array.from(dateMap.entries())
    .map(([date, counts]) => ({
      date,
      ...counts,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
};

/**
 * Get summary statistics for Support Admin dashboard
 */
export interface TaskSummaryStats {
  total_tasks: number;
  pending: number;
  in_progress: number;
  completed: number;
  cancelled: number;
  avg_resolution_days: number;
  overdue_count: number;
  // Count of tasks with agent_id === null (Unassigned triage queue).
  // Added 2026-05-15 alongside the agent_id-nullable migration.
  unassigned: number;
  tasks_by_agent: Array<{
    agent_id: string;
    agent_name: string;
    count: number;
  }>;
}

export const getTaskSummaryStats = async (
  filters?: TaskAnalyticsFilters,
): Promise<TaskSummaryStats> => {
  // Paginated fetch — without this the dashboard headline was capped at the
  // PostgREST default of 1000 rows. Prod has ~3,925 tasks, so the operator
  // was seeing "1000 total tasks" with per-status counts that summed
  // consistently to that capped number but didn't reflect reality.
  const buildSummaryQuery = () => {
    let q = supabase
      .from("support_tasks")
      .select(
        "id, status, created_at, completed_at, due_date, agent_id, agent:support_agents!support_tasks_agent_id_fkey(name)",
      );
    q = applyTaskTypeFilter(q, filters?.taskType);
    if (filters?.agentId) {
      q = q.eq("agent_id", filters.agentId);
    }
    if (filters?.startDate) {
      q = q.gte("created_at", filters.startDate);
    }
    if (filters?.endDate) {
      q = q.lte("created_at", filters.endDate + "T23:59:59");
    }
    return q;
  };

  type SummaryTaskRow = {
    id: string;
    status: string;
    created_at: string;
    completed_at: string | null;
    due_date: string | null;
    agent_id: string | null;
    agent: { name: string } | null;
  };

  const allTasks: SummaryTaskRow[] = [];
  let summaryOffset = 0;
  const hasMore = true;
  while (hasMore) {
    const { data, error } = await buildSummaryQuery()
      .order("id", { ascending: true })
      .range(summaryOffset, summaryOffset + SUPPORT_TASKS_PAGE_SIZE - 1);

    if (error) {
      console.error("Error fetching summary stats:", error);
      throw error;
    }

    const page = (data ?? []) as unknown as SummaryTaskRow[];
    if (page.length === 0) {
      break;
    }
    if (allTasks.length + page.length > SUPPORT_TASKS_HARD_CAP) {
      throw new Error(
        `Support task summary exceeds ${SUPPORT_TASKS_HARD_CAP} rows — narrow the filters before retrying.`,
      );
    }
    allTasks.push(...page);
    if (page.length < SUPPORT_TASKS_PAGE_SIZE) {
      break;
    }
    summaryOffset += SUPPORT_TASKS_PAGE_SIZE;
  }

  const today = new Date().toISOString().split("T")[0];
  let totalResolutionDays = 0;
  let resolvedCount = 0;

  const statusCounts = {
    pending: 0,
    in_progress: 0,
    completed: 0,
    cancelled: 0,
  };
  const agentCounts = new Map<string, { name: string; count: number }>();
  let overdueCount = 0;
  let unassignedCount = 0;

  for (const task of allTasks) {
    // Count by status
    if (task.status in statusCounts) {
      statusCounts[task.status as keyof typeof statusCounts]++;
    }

    // Count overdue
    if (
      task.due_date &&
      task.due_date < today &&
      task.status !== "completed" &&
      task.status !== "cancelled"
    ) {
      overdueCount++;
    }

    // Calculate resolution time
    if (task.completed_at && task.created_at) {
      const days = Math.ceil(
        (new Date(task.completed_at).getTime() -
          new Date(task.created_at).getTime()) /
          (1000 * 60 * 60 * 24),
      );
      totalResolutionDays += days;
      resolvedCount++;
    }

    // Count by agent (only assigned tasks bucket here; unassigned counted below)
    if (task.agent_id && task.agent) {
      if (!agentCounts.has(task.agent_id)) {
        agentCounts.set(task.agent_id, { name: task.agent.name, count: 0 });
      }
      agentCounts.get(task.agent_id)!.count++;
    } else if (task.agent_id === null) {
      unassignedCount++;
    }
  }

  return {
    total_tasks: allTasks.length,
    ...statusCounts,
    avg_resolution_days:
      resolvedCount > 0 ? totalResolutionDays / resolvedCount : 0,
    overdue_count: overdueCount,
    unassigned: unassignedCount,
    tasks_by_agent: Array.from(agentCounts.entries())
      .map(([agent_id, data]) => ({
        agent_id,
        agent_name: data.name,
        count: data.count,
      }))
      .sort((a, b) => b.count - a.count),
  };
};

/**
 * Get interactions linked to a support task
 */
export const getLinkedInteractions = async (
  taskId: UUID,
): Promise<
  Array<{
    id: UUID;
    type: string;
    method?: string;
    interaction_date: string;
    outcome?: string;
    description?: string;
  }>
> => {
  const { data, error } = await supabase
    .from("interactions")
    .select("id, type, method, interaction_date, outcome, description")
    .eq("support_task_id", taskId)
    .order("interaction_date", { ascending: false });

  if (error) {
    console.error("Error fetching linked interactions:", error);
    throw error;
  }

  return data || [];
};

/**
 * Get activity counts for multiple tasks
 * Returns a map of task_id -> activity count
 */
export const getActivityCountsForTasks = async (
  taskIds: UUID[],
): Promise<Map<UUID, number>> => {
  if (taskIds.length === 0) {
    return new Map();
  }

  // Aggregated server-side behind a uuid[] RPC. The previous form passed every visible task id
  // into a PostgREST `.in()` filter; at 960 pending tasks that is ~35 KB of query string and the
  // gateway answered HTTP 400 on EVERY load of /support-tasks. It failed soft — the catch below
  // returned an empty Map — so every row silently rendered an activity count of 0.
  // The RPC is SECURITY INVOKER, so RLS on interactions still applies exactly as it did here.
  const { data, error } = await supabase.rpc(
    "get_support_task_activity_counts",
    {
      p_task_ids: taskIds,
    },
  );

  if (error) {
    console.error("Error fetching activity counts:", error);
    return new Map();
  }

  // The RPC returns ONE pre-aggregated row per task, not one row per interaction, so take the
  // count it reports. Incrementing by 1 per row here (as the old row-per-interaction shape
  // required) would report every task as having exactly 1 activity.
  const counts = new Map<UUID, number>();
  for (const row of (data ?? []) as {
    support_task_id: UUID | null;
    activity_count: number | string;
  }[]) {
    const taskId = row.support_task_id;
    if (taskId) {
      counts.set(taskId, Number(row.activity_count) || 0);
    }
  }

  return counts;
};

/**
 * Get resolution templates for quick task completion
 * Returns unique templates ordered by display_order
 */
export const getResolutionTemplates = async (
  taskType?: SupportTaskType,
): Promise<ResolutionTemplate[]> => {
  let query = supabase
    .from("support_resolution_templates")
    .select("*")
    .eq("is_active", true)
    .order("display_order", { ascending: true });

  // Filter by task type if specified
  if (taskType) {
    query = query.or(`task_type.eq.${taskType},task_type.is.null`);
  }

  const { data, error } = await query;

  if (error) {
    console.error("Error fetching resolution templates:", error);
    throw error;
  }

  // Deduplicate by name (in case of duplicates in DB)
  const uniqueTemplates = new Map<string, ResolutionTemplate>();
  for (const template of data || []) {
    if (!uniqueTemplates.has(template.name)) {
      uniqueTemplates.set(template.name, template);
    }
  }

  return Array.from(uniqueTemplates.values());
};

// =====================================================
// AGENT MANAGEMENT FUNCTIONS
// =====================================================

/**
 * Get all agents with their supervisor information
 * Used for the agent management UI
 */
export const getAllAgentsWithSupervisors = async (): Promise<
  Array<{
    id: UUID;
    name: string;
    email: string;
    user_id: UUID;
    supervisor_id: UUID | null;
    supervisor: { id: UUID; name: string; email: string } | null;
    created_at: string;
  }>
> => {
  // 2026-06-02: filter out soft-deleted agents (is_active=false) so the
  // management panel only shows live agents. Restoration of inactive
  // agents is a separate flow (not in this PR).
  const { data: agents, error } = await supabase
    .from("support_agents")
    .select("id, name, email, user_id, supervisor_id, created_at")
    .eq("is_active", true)
    .order("name", { ascending: true });

  if (error) {
    console.error("Error fetching agents:", error);
    throw error;
  }

  if (!agents || agents.length === 0) {
    return [];
  }

  // Build a map of agent ID to agent for supervisor lookup
  const agentMap = new Map(agents.map((a) => [a.id, a]));

  // Add supervisor info to each agent
  return agents.map((agent) => ({
    ...agent,
    supervisor: agent.supervisor_id
      ? agentMap.get(agent.supervisor_id)
        ? {
            id: agentMap.get(agent.supervisor_id)!.id,
            name: agentMap.get(agent.supervisor_id)!.name,
            email: agentMap.get(agent.supervisor_id)!.email,
          }
        : null
      : null,
  }));
};

/**
 * Update an agent's display name. Trims + collapses whitespace so the
 * common double-space typo silently corrects on any save. Server-side
 * RLS enforces support_admin/admin/superadmin only.
 */
export const updateAgentName = async (
  agentId: UUID,
  name: string,
): Promise<void> => {
  const trimmed = name.trim().replace(/\s+/g, " ");
  if (!trimmed) {
    throw new Error("Agent name cannot be empty");
  }
  const { error } = await supabase
    .from("support_agents")
    .update({ name: trimmed, updated_at: new Date().toISOString() })
    .eq("id", agentId);
  if (error) {
    console.error("Error updating agent name:", error);
    throw error;
  }
};

/**
 * Soft-delete an agent (sets is_active=false) and reassign every
 * outstanding reference (open tasks, escalations, contact assignment,
 * supervisor links) atomically via the SECURITY DEFINER RPC. Caller
 * MUST be support_admin/admin/superadmin or the RPC throws.
 */
export interface SoftDeleteAgentResult {
  soft_deleted: boolean;
  agent_id: UUID;
  agent_name: string;
  target_agent_id: UUID;
  target_agent_name: string;
  tasks_reassigned: number;
  escalations_reassigned: number;
  contacts_reassigned: number;
  supervisor_links_cleared: number;
}

export const softDeleteAgentAndReassign = async (
  agentId: UUID,
  targetAgentId: UUID,
): Promise<SoftDeleteAgentResult> => {
  const { data, error } = await supabase.rpc(
    "soft_delete_support_agent_and_reassign",
    { p_agent_id: agentId, p_target_agent_id: targetAgentId },
  );
  if (error) {
    console.error("Error soft-deleting agent:", error);
    throw error;
  }
  return data as SoftDeleteAgentResult;
};

/**
 * 2026-06-09: agent delete-to-unassigned. Deletes the agent and routes
 * their open work to the Unassigned bucket (agent_id = NULL,
 * escalated_to = NULL), nulls assigned_agent_id on contacts, and
 * clears supervisor_id on subordinates. No transfer target — operator
 * decision codified in migration 2026-06-09-003.
 */
export interface SoftDeleteAgentToUnassignedResult {
  soft_deleted: boolean;
  agent_id: UUID;
  agent_name: string;
  routed_to: "unassigned";
  tasks_unassigned: number;
  escalations_cleared: number;
  contacts_unassigned: number;
  supervisor_links_cleared: number;
}

export const softDeleteAgentToUnassigned = async (
  agentId: UUID,
): Promise<SoftDeleteAgentToUnassignedResult> => {
  const { data, error } = await supabase.rpc(
    "soft_delete_support_agent_to_unassigned",
    { p_agent_id: agentId },
  );
  if (error) {
    console.error("Error soft-deleting agent to unassigned:", error);
    throw error;
  }
  return data as SoftDeleteAgentToUnassignedResult;
};

/**
 * Retroactive cleanup for agents that had is_active=false toggled
 * directly without going through the canonical soft-delete RPC. Per
 * docs/AUDIT_PROMPT_SOFT_DELETE_AGENTS_AND_CANCEL_INVITATIONS.md.
 *
 * Same reassignment as soft_delete_support_agent_and_reassign EXCEPT
 * the source must already be is_active=false (this RPC won't flip it
 * a second time). Distinguished in the audit log by kind='cleanup'.
 */
export interface CleanupStrandedAgentResult {
  ok: boolean;
  agent_id: UUID;
  target_agent_id: UUID;
  kind: "cleanup";
  tasks_reassigned: number;
  escalations_reassigned: number;
  contacts_reassigned: number;
  supervisor_links_cleared: number;
}

export const cleanupStrandedInactiveAgent = async (
  agentId: UUID,
  targetAgentId: UUID,
): Promise<CleanupStrandedAgentResult> => {
  const { data, error } = await supabase.rpc(
    "cleanup_stranded_inactive_agent",
    { p_agent_id: agentId, p_target_agent_id: targetAgentId },
  );
  if (error) {
    console.error("Error cleaning up stranded agent:", error);
    throw error;
  }
  return data as CleanupStrandedAgentResult;
};

/**
 * 2026-10-26 (migration 1080): reactivate one inactive agent so they start
 * receiving new tasks again. Same authorization as delete/cleanup (active
 * admin / superadmin / support_admin, enforced in the RPC). The RPC refuses
 * with `AGENT_REACTIVATE_REFUSED:<CODE>` when the agent was deleted or merged
 * into another agent, has no CRM user, or the user is inactive or no longer
 * in a support role.
 */
export interface ReactivateAgentResult {
  reactivated: boolean;
  agent_id: UUID;
  agent_name: string;
  previous_reason: string | null;
}

const REACTIVATE_REFUSAL_MESSAGES: Record<string, string> = {
  NOT_FOUND: "This agent no longer exists.",
  ALREADY_ACTIVE: "This agent is already active.",
  DELETED_OR_MERGED:
    "This agent was deleted or merged into another agent, so their work was moved. They can't be reactivated.",
  NO_PROFILE:
    "This agent isn't linked to a CRM user, so they can't be reactivated.",
  PROFILE_INACTIVE:
    "This agent's CRM user is deactivated. Reactivate the user on the Users page first.",
  CHANGED:
    "This agent was changed by someone else at the same moment. Refresh the list and try again.",
  ROLE_NOT_SUPPORT:
    "This agent's CRM user no longer has the Support Agent or Support Admin role. Change the role on the Users page first.",
};

/** Turn an RPC error message into the specific, human reason shown in the toast. */
export const describeReactivateAgentError = (message: string): string => {
  const m = /AGENT_REACTIVATE_REFUSED:([A-Z_]+)/.exec(message ?? "");
  if (m && REACTIVATE_REFUSAL_MESSAGES[m[1]]) {
    return `Couldn't reactivate: ${REACTIVATE_REFUSAL_MESSAGES[m[1]]}`;
  }
  if (/Insufficient permissions/i.test(message ?? "")) {
    return "Couldn't reactivate: only admins and support admins can reactivate agents.";
  }
  return `Couldn't reactivate: ${message || "unknown error"}`;
};

export const reactivateSupportAgent = async (
  agentId: UUID,
): Promise<ReactivateAgentResult> => {
  const { data, error } = await supabase.rpc("reactivate_support_agent", {
    p_agent_id: agentId,
  });
  if (error) {
    console.error("Error reactivating agent:", error);
    throw error;
  }
  return data as ReactivateAgentResult;
};

/**
 * Fetch agents including inactive (soft-deleted / directly-deactivated)
 * rows. Powers the "Show inactive" toggle on AgentManagementPanel and
 * the cleanup action surface for stranded inactive agents. The list
 * carries is_active and reference counts so the panel can render the
 * read-only state + show the "Cleanup needed" badge.
 */
export const getAllAgentsIncludingInactiveWithSupervisors = async (): Promise<
  Array<{
    id: UUID;
    name: string;
    email: string;
    user_id: UUID;
    is_active: boolean;
    supervisor_id: UUID | null;
    supervisor: { id: UUID; name: string; email: string } | null;
    created_at: string;
    has_stranded_refs: boolean;
    /** Inactive AND recorded in support_agent_deletions (deleted / merged): can't be reactivated. */
    is_deleted_or_merged: boolean;
  }>
> => {
  const { data: agents, error } = await supabase
    .from("support_agents")
    .select("id, name, email, user_id, is_active, supervisor_id, created_at")
    .order("name", { ascending: true });
  if (error) {
    throw error;
  }
  if (!agents || agents.length === 0) {
    return [];
  }

  // For inactive agents, surface a stranded-refs flag. Two queries
  // (open tasks + contacts) are enough for the UI badge; the actual
  // cleanup RPC re-checks atomically. Skip for active agents — they
  // shouldn't be cleaned up via this surface.
  const inactiveIds = agents.filter((a) => !a.is_active).map((a) => a.id);
  const strandedIds = new Set<UUID>();
  const deletedIds = new Set<UUID>();
  if (inactiveIds.length > 0) {
    const [taskRefs, contactRefs, deletionRefs] = await Promise.all([
      supabase
        .from("support_tasks")
        .select("agent_id")
        .in("agent_id", inactiveIds)
        .not("status", "in", "(completed,cancelled)"),
      supabase
        .from("contacts")
        .select("assigned_agent_id")
        .in("assigned_agent_id", inactiveIds),
      // Deleted / merged agents can't be reactivated (the RPC refuses too);
      // knowing it here lets the panel say so instead of offering a button
      // that would fail. On a read error the flag stays false and the RPC's
      // refusal toast still explains.
      supabase
        .from("support_agent_deletions")
        .select("deleted_agent_id")
        .in("deleted_agent_id", inactiveIds),
    ]);
    for (const row of (deletionRefs.data ?? []) as Array<{
      deleted_agent_id: UUID;
    }>) {
      deletedIds.add(row.deleted_agent_id);
    }
    for (const row of (taskRefs.data ?? []) as Array<{ agent_id: UUID }>) {
      strandedIds.add(row.agent_id);
    }
    for (const row of (contactRefs.data ?? []) as Array<{
      assigned_agent_id: UUID;
    }>) {
      strandedIds.add(row.assigned_agent_id);
    }
  }

  const agentMap = new Map(agents.map((a) => [a.id, a]));
  return agents.map((agent) => ({
    ...agent,
    supervisor: agent.supervisor_id
      ? agentMap.get(agent.supervisor_id)
        ? {
            id: agentMap.get(agent.supervisor_id)!.id,
            name: agentMap.get(agent.supervisor_id)!.name,
            email: agentMap.get(agent.supervisor_id)!.email,
          }
        : null
      : null,
    has_stranded_refs: strandedIds.has(agent.id),
    is_deleted_or_merged: !agent.is_active && deletedIds.has(agent.id),
  }));
};

/**
 * Find active agents whose normalized full name matches the source
 * agent's. Used to seed the "transfer target" default suggestion when
 * deleting a duplicate-entry agent (DeAngela Davis ×3, Patricia Bowens,
 * etc.). Excludes the source agent itself. Full-name match only —
 * first-name match is too loose.
 */
export const findSameNameAgents = async (
  sourceName: string,
  excludeAgentId: UUID,
): Promise<Array<{ id: UUID; name: string; email: string }>> => {
  const normalized = sourceName.trim().replace(/\s+/g, " ").toLowerCase();
  if (!normalized) {
    return [];
  }
  const { data, error } = await supabase
    .from("support_agents")
    .select("id, name, email")
    .eq("is_active", true)
    .neq("id", excludeAgentId);
  if (error) {
    console.error("Error finding same-name agents:", error);
    throw error;
  }
  return (data ?? []).filter((a) => {
    const candidateNorm = (a.name ?? "")
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase();
    return candidateNorm === normalized;
  });
};

/**
 * Update an agent's supervisor
 */
export const updateAgentSupervisor = async (
  agentId: UUID,
  supervisorId: UUID | null,
): Promise<void> => {
  const { error } = await supabase
    .from("support_agents")
    .update({ supervisor_id: supervisorId })
    .eq("id", agentId);

  if (error) {
    console.error("Error updating agent supervisor:", error);
    throw error;
  }
};

/**
 * Get agents who can be supervisors
 * Includes: support_admin, superadmin, admin roles (any role that should be able to supervise)
 */
export const getPotentialSupervisors = async (): Promise<
  Array<{ id: UUID; name: string; email: string }>
> => {
  // Get users with supervisor-capable roles from profiles table
  const { data: adminUsers, error: usersError } = await supabase
    .from("profiles")
    .select("id")
    .in("role", ["support_admin", "superadmin", "admin"]);

  if (usersError) {
    console.error("Error fetching admin users:", usersError);
    throw usersError;
  }

  if (!adminUsers || adminUsers.length === 0) {
    return [];
  }

  const adminUserIds = adminUsers.map((u) => u.id);

  // Get support agents who have admin-level roles
  const { data, error } = await supabase
    .from("support_agents")
    .select("id, name, email")
    .in("user_id", adminUserIds)
    .order("name", { ascending: true });

  if (error) {
    console.error("Error fetching potential supervisors:", error);
    throw error;
  }

  return data || [];
};

// =====================================================
// ESCALATION FUNCTIONS
// =====================================================

/**
 * Escalate a task to the agent's supervisor
 */
export const escalateTask = async (
  taskId: UUID,
  reason: string,
): Promise<SupportTask | null> => {
  // First get the task to find the agent
  const { data: task, error: taskError } = await supabase
    .from("support_tasks")
    .select(
      `
      *,
      agent:support_agents!support_tasks_agent_id_fkey(id, name, email, supervisor_id)
    `,
    )
    .eq("id", taskId)
    .single();

  if (taskError || !task) {
    console.error("Error fetching task for escalation:", taskError);
    throw refusalIfInvisible(taskError);
  }

  const agent = task.agent as {
    id: string;
    name: string;
    email: string;
    supervisor_id: string | null;
  };

  if (!agent?.supervisor_id) {
    throw new Error(
      "Agent does not have a supervisor assigned. Please contact an administrator.",
    );
  }

  // Update the task with escalation info. Prove a row was written (see
  // SupportTaskNotUpdatedError) instead of asking the UPDATE for embeds with .single(),
  // which turned an RLS refusal into a raw "JSON object requested..." 406. (2026-10-03)
  try {
    await updateSupportTaskRow(taskId, {
      escalated_to: agent.supervisor_id,
      escalated_at: new Date().toISOString(),
      escalation_reason: reason,
      original_agent_id: task.agent_id,
    });
  } catch (error) {
    console.error("Error escalating task:", error);
    throw error;
  }

  return fetchTaskWithEscalationEmbeds(taskId);
};

/**
 * De-escalate a task (return to original agent)
 * Only the supervisor (escalated_to) can de-escalate
 */
export const deescalateTask = async (
  taskId: UUID,
): Promise<SupportTask | null> => {
  // First get the task to verify it's escalated
  const { data: task, error: taskError } = await supabase
    .from("support_tasks")
    .select("*, original_agent_id")
    .eq("id", taskId)
    .single();

  if (taskError || !task) {
    console.error("Error fetching task for de-escalation:", taskError);
    throw refusalIfInvisible(taskError);
  }

  if (!task.escalated_to || !task.original_agent_id) {
    throw new Error("Task is not currently escalated");
  }

  // Clear escalation fields (same zero-rows contract as escalateTask).
  try {
    await updateSupportTaskRow(taskId, {
      escalated_to: null,
      escalated_at: null,
      escalation_reason: null,
      original_agent_id: null,
    });
  } catch (error) {
    console.error("Error de-escalating task:", error);
    throw error;
  }

  return fetchTaskWithEscalationEmbeds(taskId);
};

/**
 * Get tasks escalated to a specific agent
 */
export const getEscalatedTasks = async (
  supervisorAgentId: UUID,
  filter?: TaskFilter,
  sortField: TaskSortField = "due_date",
  sortOrder: TaskSortOrder = "asc",
): Promise<SupportTask[]> => {
  let query = supabase
    .from("support_tasks")
    .select(
      `
      *,
      agent:support_agents!support_tasks_agent_id_fkey(id, name, email),
      contact:contacts!support_tasks_contact_id_fkey(id, first_name, last_name, email, phone_number),
      escalated_to_agent:support_agents!support_tasks_escalated_to_fkey(id, name, email),
      original_agent:support_agents!support_tasks_original_agent_id_fkey(id, name, email)
    `,
    )
    .eq("escalated_to", supervisorAgentId)
    .in("status", ["pending", "in_progress"]);

  // Apply task type filter
  query = applyTaskTypeFilter(query, filter?.taskType);

  // Apply sorting
  const ascending = sortOrder === "asc";
  switch (sortField) {
    case "due_date":
      query = query.order("due_date", { ascending });
      break;
    case "customer_name":
      query = query.order("customer_snapshot->name", { ascending });
      break;
    case "task_type":
      query = query.order("task_type", { ascending });
      break;
    default:
      query = query.order("due_date", { ascending });
  }

  const { data, error } = await query;

  if (error) {
    console.error("Error fetching escalated tasks:", error);
    throw error;
  }

  let tasks = data || [];

  // Apply search filter
  if (filter?.searchQuery) {
    tasks = tasks.filter((task) =>
      matchesSupportTaskSearch(task, filter.searchQuery!),
    );
  }

  return applyPriorityBucketSort(tasks);
};

/**
 * Get count of tasks escalated to a supervisor
 */
export const getEscalatedTaskCount = async (
  supervisorAgentId: UUID,
): Promise<number> => {
  const { count, error } = await supabase
    .from("support_tasks")
    .select("*", { count: "exact", head: true })
    .eq("escalated_to", supervisorAgentId)
    .in("status", ["pending", "in_progress"]);

  if (error) {
    console.error("Error fetching escalated task count:", error);
    throw error;
  }

  return count || 0;
};

/**
 * Create a manual support task
 */
export interface CreateManualTaskInput {
  agentId: UUID;
  contactId: UUID;
  taskType: SupportTaskType;
  dueDate?: string;
  notes?: string;
  createdBy: UUID;
  parentTaskId?: UUID;
}

export const createManualTask = async (
  input: CreateManualTaskInput,
): Promise<SupportTask> => {
  const {
    agentId,
    contactId,
    taskType,
    dueDate,
    notes,
    createdBy,
    parentTaskId,
  } = input;

  // Fetch contact details for customer snapshot.
  // 2026-06-02: include membership_id so the manual-creation path matches
  // the create-support-task API's snapshot shape — without it, the
  // task-detail view's "Member ID" link doesn't render for manually
  // created tasks (it reads from customer_snapshot.member_id).
  const { data: contact, error: contactError } = await supabase
    .from("contacts")
    .select(
      "id, first_name, last_name, email, phone_number, member_status, membership_id",
    )
    .eq("id", contactId)
    .single();

  if (contactError || !contact) {
    throw new Error("Contact not found");
  }

  // Build customer snapshot from contact
  const customerSnapshot: CustomerSnapshot = {
    id: contact.id,
    name: `${contact.first_name || ""} ${contact.last_name || ""}`.trim(),
    email: contact.email || undefined,
    phone: contact.phone_number || undefined,
    account_status: contact.member_status || undefined,
    member_id: contact.membership_id || undefined,
  };

  // Create the task. Retry on transient contention (serialization / statement
  // timeout / resource / connection) so pushing a support task doesn't fail just
  // because a large send was hammering the DB at that moment.
  const { data, error } = await callWithRetry(
    () =>
      supabase
        .from("support_tasks")
        .insert({
          agent_id: agentId,
          contact_id: contactId,
          task_type: taskType,
          status: "pending",
          due_date: dueDate || null,
          customer_snapshot: customerSnapshot,
          notes: notes || null,
          created_by: createdBy,
          parent_task_id: parentTaskId || null,
        })
        .select(
          `
      *,
      agent:support_agents!support_tasks_agent_id_fkey(id, name, email),
      contact:contacts!support_tasks_contact_id_fkey(id, first_name, last_name, email, phone_number)
    `,
        )
        .single(),
    { retries: 2, baseDelayMs: 400 },
  );

  if (error) {
    console.error("Error creating manual task:", error);
    logRpcError("support_task_create", "support_tasks.insert", error, {
      task_type: taskType,
      contact_id: contactId,
    });
    throw error;
  }

  return data as SupportTask;
};

/**
 * Per-agent activity summary for the Support Tasks admin view. Returns one row per active
 * support agent with tasks_closed (completed tasks whose completed_at is in the window).
 * "Saves" and "Sales" are not computed yet (no data model / attribution rule) — the UI shows
 * them as "Coming Soon". Backed by the SECURITY DEFINER RPC get_agent_activity_summary,
 * which fails closed on role (support_admin/admin/superadmin).
 */
export interface AgentActivityRow {
  agent_id: string;
  name: string | null;
  tasks_closed: number;
}

export async function getAgentActivitySummary(
  dateFrom?: string | null,
  dateTo?: string | null,
): Promise<AgentActivityRow[]> {
  const { data, error } = await supabase.rpc("get_agent_activity_summary", {
    p_date_from: dateFrom ?? null,
    p_date_to: dateTo ?? null,
  });
  if (error) {
    throw error;
  }
  return (data ?? []) as AgentActivityRow[];
}

/**
 * Count the contacts assigned to a support agent.
 *
 * ⚠️ COLUMN CHOICE MATTERS. `contacts` carries two agent columns and only one is
 * written: `assigned_support_agent_id` (1,577,287 rows on PROD) is authoritative,
 * while `assigned_agent_id` is vestigial with ZERO non-null rows. Querying the
 * latter returns 0 for every agent without erroring — which is exactly how the
 * contacts-by-agent filter shipped broken. Keep this on the support column.
 *
 * Uses head:true + exact count so we transfer a number, not 35K rows.
 */
export const getAgentAssignedContactCount = async (
  agentId: UUID,
): Promise<number> => {
  const { count, error } = await supabase
    .from("contacts")
    .select("id", { count: "exact", head: true })
    .eq("assigned_support_agent_id", agentId);

  if (error) {
    console.error("Error counting agent-assigned contacts:", error);
    throw error;
  }
  return count ?? 0;
};

/**
 * Fetch a single support agent's public-facing fields by id.
 *
 * Used by the contact page to label the "Assigned Support Concierge" link.
 * Returns null when the id resolves to nothing, so a contact pointing at a
 * deleted agent renders no link rather than a broken one.
 */
export const getSupportAgentById = async (
  agentId: UUID,
): Promise<{ id: UUID; name: string; is_active: boolean } | null> => {
  const { data, error } = await supabase
    .from("support_agents")
    .select("id, name, is_active")
    .eq("id", agentId)
    .maybeSingle();

  if (error) {
    console.error("Error fetching support agent:", error);
    throw error;
  }
  return (
    (data as { id: UUID; name: string; is_active: boolean } | null) ?? null
  );
};
